import { api } from "../api";
import type { BillingProduct } from "../api";
import type { Subscription } from "../types";
import { Icons } from "../utils/icons";
import { esc } from "../utils/html";

interface FeatureInfo {
  product: BillingProduct;
  productId: string;
  label: string;
  description: string;
  icon: string;
}

/**
 * Module ohne hinterlegte Produkt-ID werden ausgeblendet: ohne STRIPE_PRODUCT_* liefe
 * der Checkout ins Leere und die Karte wäre nicht buchbar.
 */
function buildFeatures(products: Record<BillingProduct, string>): FeatureInfo[] {
  const catalog: FeatureInfo[] = [
    {
      product: "planung",
      productId: products.planung,
      label: "Praktikumsplanung",
      description: "Automatische und manuelle Planung von Praktikumseinsätzen für deine Schüler.",
      icon: Icons.calendarDays,
    },
    {
      product: "schuelerPortal",
      productId: products.schuelerPortal,
      label: "Schüler-Portal",
      description: "Schüler können sich einloggen, ihre Planung einsehen und Wunschlisten erstellen.",
      icon: Icons.user,
    },
    {
      product: "stundenplan",
      productId: products.stundenplan,
      label: "Stundenplan",
      description: "Dozenten, Fächer und Unterricht je Klasse planen, inklusive Anwesenheiten der Dozenten.",
      icon: Icons.clipboardDocumentList,
    },
  ];
  return catalog.filter(f => !!f.productId);
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "–";
  return new Date(dateStr).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function statusBadge(status: Subscription["status"]): string {
  const map: Record<string, { label: string; cls: string }> = {
    trialing:          { label: "Testphase",      cls: "badge-info" },
    active:            { label: "Aktiv",           cls: "badge-success" },
    past_due:          { label: "Zahlung offen",   cls: "badge-warning" },
    canceled:          { label: "Gekündigt",       cls: "badge-error" },
    incomplete:        { label: "Unvollständig",   cls: "badge-warning" },
    incomplete_expired:{ label: "Abgelaufen",      cls: "badge-error" },
    unpaid:            { label: "Nicht bezahlt",   cls: "badge-error" },
  };
  const { label, cls } = map[status] || { label: status, cls: "badge-info" };
  return `<span class="badge ${cls}">${label}</span>`;
}

function renderFeatureCard(feature: FeatureInfo, sub: Subscription | undefined, hasPortal: boolean): string {
  const isActive = sub && ["active", "trialing", "past_due"].includes(sub.status);
  const isCanceled = sub?.status === "canceled";

  let dateInfo = "";
  if (sub) {
    if (sub.status === "trialing" && sub.trialEnd) {
      dateInfo = `<div style="font-size:.8rem;color:var(--gray-500);margin-top:.25rem">Testphase bis: ${formatDate(sub.trialEnd)}</div>`;
    } else if (isActive && sub.currentPeriodEnd) {
      dateInfo = `<div style="font-size:.8rem;color:var(--gray-500);margin-top:.25rem">Nächste Abrechnung: ${formatDate(sub.currentPeriodEnd)}</div>`;
    } else if (isCanceled && sub.canceledAt) {
      dateInfo = `<div style="font-size:.8rem;color:var(--gray-500);margin-top:.25rem">Gekündigt am: ${formatDate(sub.canceledAt)}</div>`;
    }
  }

  const subscribeBtn = !sub || isCanceled
    ? `<button class="btn btn-primary btn-subscribe" data-product="${feature.product}">Jetzt abonnieren</button>`
    : "";

  const manageBtn = isActive && hasPortal
    ? `<button class="btn btn-secondary btn-portal">Abonnement verwalten</button>`
    : "";

  return `
    <div class="card" style="padding:1.5rem">
      <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:1rem;flex-wrap:wrap">
        <div style="display:flex;align-items:center;gap:.75rem">
          <span class="mod-icon" style="width:1.75rem;height:1.75rem;color:var(--gray-900)">${feature.icon}</span>
          <div>
            <div style="font-weight:600;font-size:1rem">${esc(feature.label)}</div>
            <div style="font-size:.875rem;color:var(--gray-500);margin-top:.125rem">${esc(feature.description)}</div>
          </div>
        </div>
        <div style="display:flex;flex-direction:column;align-items:flex-end;gap:.5rem">
          ${sub ? statusBadge(sub.status) : '<span class="badge" style="background:var(--gray-100);color:var(--gray-500)">Nicht abonniert</span>'}
          ${dateInfo}
        </div>
      </div>
      ${subscribeBtn || manageBtn
        ? `<div style="display:flex;gap:.75rem;margin-top:1rem;flex-wrap:wrap">${subscribeBtn}${manageBtn}</div>`
        : ""}
    </div>`;
}

async function subscribeFeature(product: BillingProduct) {
  try {
    const { url } = await api.billing.checkout(product);
    if (url) window.location.href = url;
  } catch (err: any) {
    alert(`Fehler beim Starten des Checkouts: ${err.message}`);
  }
}

async function openPortal() {
  try {
    const { url } = await api.billing.portal();
    if (url) window.open(url, "_blank");
  } catch (err: any) {
    alert(`Fehler beim Öffnen des Kundenportals: ${err.message}`);
  }
}

export async function render(el: HTMLElement) {
  el.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;height:200px"><div class="spinner"></div></div>`;

  let subscriptions: Subscription[] = [];
  let features: FeatureInfo[] = [];
  try {
    const [config, subs] = await Promise.all([
      api.billing.config(),
      api.billing.subscriptions(),
    ]);
    subscriptions = subs;
    features = buildFeatures(config.products);
  } catch (err: any) {
    el.innerHTML = `<div class="alert alert-error">Fehler beim Laden der Abonnements: ${esc(err.message)}</div>`;
    return;
  }

  const hasStripePortal = subscriptions.some(s => s.stripeSubscriptionId && !["canceled"].includes(s.status));

  // Prüfe auf Checkout-Ergebnis in URL
  const params = new URLSearchParams(window.location.hash.split("?")[1] || "");
  let checkoutBanner = "";
  if (params.get("checkout") === "success") {
    checkoutBanner = `<div class="alert" style="background:#d1fae5;border:1px solid #6ee7b7;color:#065f46;padding:.75rem 1rem;border-radius:.5rem;margin-bottom:1rem">
      ✓ Abonnement erfolgreich gestartet! Die Aktivierung des Features kann einige Sekunden dauern.
    </div>`;
    // Hash bereinigen
    window.history.replaceState(null, "", window.location.pathname + "#/abonnements");
  } else if (params.get("checkout") === "canceled") {
    checkoutBanner = `<div class="alert alert-warning" style="padding:.75rem 1rem;border-radius:.5rem;margin-bottom:1rem">
      Checkout abgebrochen. Kein Abonnement wurde angelegt.
    </div>`;
    window.history.replaceState(null, "", window.location.pathname + "#/abonnements");
  }

  const featureCards = features.map(feature => {
    const sub = subscriptions.find(s => s.stripeProductId === feature.productId);
    return renderFeatureCard(feature, sub, hasStripePortal);
  }).join("");

  el.innerHTML = `
    <div class="page-header">
      <div>
        <h1>Abonnements</h1>
        <div style="font-size:.875rem;color:var(--gray-500);margin-top:.25rem">
          Verwalte deine monatlich abonnierten Features
        </div>
      </div>
    </div>

    ${checkoutBanner}

    <div style="display:grid;gap:1rem;margin-bottom:1.5rem">
      ${featureCards}
    </div>

    <div class="card" style="padding:1.5rem;margin-bottom:1.5rem">
      <div style="display:flex;align-items:flex-start;gap:.75rem">
        <span class="mod-icon" style="width:1.75rem;height:1.75rem;color:var(--gray-900);flex-shrink:0">${Icons.documentText}</span>
        <div>
          <div style="font-weight:600;font-size:1rem">Bestellung per Auftragsformular</div>
          <div style="font-size:.875rem;color:var(--gray-500);margin-top:.25rem">
            Du kannst die Module auch schriftlich beauftragen, zum Beispiel wenn deine Einrichtung
            per Rechnung zahlt. Lade das Auftragsformular herunter, kreuze die gewünschten Module an,
            fülle es aus und sende es unterschrieben an
            <a href="mailto:vertrieb@meduvio.de">vertrieb@meduvio.de</a>.
            Wir bearbeiten deinen Auftrag und schalten die Module anschließend frei.
          </div>
          <a class="btn btn-secondary" href="/assets/Meduvio-Auftragsformular.pdf" download
             style="margin-top:1rem">
            <span class="icon">${Icons.arrowDownTray}</span>Auftragsformular herunterladen
          </a>
        </div>
      </div>
    </div>

    <div class="card" style="padding:1rem 1.5rem;background:var(--gray-50)">
      <div style="font-size:.875rem;color:var(--gray-500)">
        <strong>Hinweis:</strong> Abonnements beginnen mit einer 14-tägigen kostenlosen Testphase.
        Rechnungen und Zahlungsmethoden verwaltest du über das Stripe-Kundenportal
        (Schaltfläche „Abonnement verwalten").
        Bei Fragen wende dich an <a href="mailto:vertrieb@meduvio.de">vertrieb@meduvio.de</a>.
      </div>
    </div>`;

  // Event-Listener
  el.querySelectorAll(".btn-subscribe").forEach(btn => {
    btn.addEventListener("click", () => {
      const product = (btn as HTMLElement).dataset.product as BillingProduct;
      subscribeFeature(product);
    });
  });

  el.querySelectorAll(".btn-portal").forEach(btn => {
    btn.addEventListener("click", openPortal);
  });
}
